/**
 * Wave 10: Chinese Cities (PRIORITY) + Additional Global Cities
 * Shanghai, Beijing, Shenzhen, Guangzhou, Chengdu + Taipei, Hong Kong, Hyderabad, Tel Aviv, Casablanca Tech City
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling. React, Node.js, Python, cloud-native.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools. Modern stack with React, TypeScript, and cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter. From MVP to enterprise-grade mobile solutions.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, recommendation engines, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, monitoring, and 24/7 managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists, and project managers integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems. Figma-first approach with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance (ISO 27001, GDPR, HIPAA, PCI-DSS).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools, and banking applications with PCI-DSS and SOC 2 compliance.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management, and omnichannel retail solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine platforms, EHR integrations, and HIPAA-compliant health applications.' },
  { name: 'SaaS & Technology', description: 'Product development, scaling, and modernization for SaaS companies from seed to Series C+.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management, and supply chain visibility platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE10: Record<string, CityAgencyData> = {

  shanghai: {
    slug: 'software-development-agency-shanghai',
    cityName: 'Shanghai',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY/USD',
    title: {
      en: 'Software Development Agency Shanghai | Call IT Dev',
      fr: 'Agence de Développement Informatique Shanghai | Call IT Dev',
      zh: '上海软件开发公司 | Call IT Dev',
      ja: 'ソフトウェア開発会社 上海 | Call IT Dev',
      ko: '소프트웨어 개발 에이전시 상하이 | Call IT Dev',
      de: 'Softwareentwicklung Agentur Shanghai | Call IT Dev',
      ar: 'وكالة تطوير برمجيات شنغهاي | Call IT Dev',
    },
    description: {
      en: 'Top software development agency serving Shanghai, China. Custom SaaS, web, mobile, AI development. 200+ engineers, competitive rates, GDPR compliant.',
      fr: 'Agence de développement informatique pour Shanghai, Chine. SaaS, web, mobile, IA. 200+ ingénieurs, tarifs compétitifs.',
      zh: '为上海提供顶级软件开发服务。定制SaaS、Web、移动应用、AI开发。200+工程师，具有竞争力的价格，ISO 27001认证。',
      ja: '上海向けトップソフトウェア開発会社。SaaS、Web、モバイル、AI開発。200名以上のエンジニア。',
      ko: '상하이 최고의 소프트웨어 개발 에이전시. 맞춤형 SaaS, 웹, 모바일, AI 개발. 200+ 엔지니어.',
    },
    keywords: ['software development agency Shanghai', '上海软件开发公司', 'IT outsourcing Shanghai', 'nearshore development China', 'web development Shanghai', 'AI development Shanghai', 'SaaS development China', 'app development Shanghai'],
    heroTitle: {
      en: 'Software Development Agency for Shanghai',
      fr: 'Agence de Développement Informatique pour Shanghai',
      zh: '上海软件开发合作伙伴',
      ja: '上海向けソフトウェア開発エージェンシー',
      ko: '상하이를 위한 소프트웨어 개발 에이전시',
    },
    heroSubtitle: {
      en: 'Serving Shanghai\'s dynamic tech ecosystem with 200+ engineers. From fintech to e-commerce — enterprise software at competitive global rates.',
      fr: 'Au service de l\'écosystème technologique dynamique de Shanghai. 200+ ingénieurs, du fintech au e-commerce.',
      zh: '服务上海充满活力的科技生态系统，200+工程师。从金融科技到电子商务——以全球竞争力价格打造企业级软件。',
      ja: '上海のダイナミックなテックエコシステムに200名以上のエンジニアで対応。フィンテックからECまで。',
      ko: '200+ 엔지니어로 상하이의 역동적인 기술 생태계를 지원합니다.',
    },
    whyTitle: {
      en: 'Why Choose Our Agency for Shanghai Projects',
      fr: 'Pourquoi Choisir Notre Agence pour Shanghai',
      zh: '为什么选择我们的上海项目团队',
    },
    whyReasons: [
      { icon: '🏙️', title: 'Shanghai Market Expertise', description: 'Deep understanding of China\'s largest business hub. WeChat, Alipay, and local platform integrations.' },
      { icon: '🌐', title: 'Cross-Border Capabilities', description: 'Bilingual teams (Chinese/English) for international projects. Bridge between Chinese and global markets.' },
      { icon: '💰', title: 'Competitive Global Rates', description: 'Premium engineering talent at 50-60% savings vs Shanghai agency rates. Enterprise quality at scale.' },
      { icon: '🔒', title: 'Data Compliance', description: 'PIPL (China data law), GDPR, ISO 27001 compliant. Secure cross-border data handling.' },
      { icon: '⚡', title: 'Rapid Scaling', description: 'Scale from 2 to 50+ engineers in weeks. Dedicated teams for Shanghai-based enterprises.' },
      { icon: '🤖', title: 'AI & Innovation', description: 'Cutting-edge AI/ML capabilities. Computer vision, NLP, LLM integration for Chinese market applications.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you support Chinese platforms (WeChat, Alipay)?', answer: 'Yes. Our teams have extensive experience with WeChat Mini Programs, Alipay integrations, Douyin APIs, and other Chinese platform ecosystems.' },
      { question: 'How do you handle China\'s data regulations (PIPL)?', answer: 'We comply with PIPL, CSL, and GDPR. Our architecture supports data localization requirements with secure cross-border transfer mechanisms.' },
      { question: 'Can you build for both Chinese and global markets?', answer: 'Absolutely. We specialize in dual-market applications with Chinese and international versions, handling localization, compliance, and platform differences.' },
      { question: 'What is the cost comparison to Shanghai agencies?', answer: 'Senior developers in Shanghai cost ¥800-1500/hr (agency). Our rates are ¥350-600/hr for equivalent expertise, saving 50-60%.' },
      { question: 'Do you have Chinese-speaking engineers?', answer: 'Yes. We have Mandarin-speaking engineers and project managers for seamless communication with Shanghai-based teams.' },
      { question: 'How do you ensure quality for enterprise clients?', answer: 'Enterprise security standards, GDPR compliant. Mandatory code reviews, 80%+ test coverage, CI/CD pipelines, and bi-weekly sprint demos.' },
    ],
    localRate: '¥800-1500',
    ourRate: '¥350-600',
    savings: '55%+',
  },

  beijing: {
    slug: 'software-development-agency-beijing',
    cityName: 'Beijing',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY/USD',
    title: {
      en: 'Software Development Agency Beijing | Call IT Dev',
      fr: 'Agence de Développement Informatique Pékin | Call IT Dev',
      zh: '北京软件开发公司 | Call IT Dev',
      ja: 'ソフトウェア開発会社 北京 | Call IT Dev',
      ko: '소프트웨어 개발 에이전시 베이징 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Beijing. Custom SaaS, web, mobile, AI. 200+ engineers, government & enterprise projects, ISO 27001.',
      fr: 'Agence de développement informatique pour Pékin. SaaS, web, mobile, IA. 200+ ingénieurs.',
      zh: '为北京提供软件开发服务。定制SaaS、Web、移动应用、AI。200+工程师，政府及企业项目，ISO 27001认证。',
    },
    keywords: ['software development agency Beijing', '北京软件开发公司', 'IT outsourcing Beijing', 'app development Beijing', 'AI development Beijing', 'enterprise software Beijing'],
    heroTitle: {
      en: 'Software Development Agency for Beijing',
      fr: 'Agence de Développement Informatique pour Pékin',
      zh: '北京软件开发合作伙伴',
    },
    heroSubtitle: {
      en: 'Serving Beijing\'s government and enterprise sector with 200+ engineers. AI, cloud, and digital transformation at competitive rates.',
      fr: 'Au service du secteur gouvernemental et entreprise de Pékin. IA, cloud et transformation digitale.',
      zh: '服务北京政府与企业客户，200+工程师。AI、云计算和数字化转型，价格极具竞争力。',
    },
    whyTitle: {
      en: 'Why Choose Our Agency for Beijing Projects',
      fr: 'Pourquoi Choisir Notre Agence pour Pékin',
      zh: '为什么选择我们的北京项目团队',
    },
    whyReasons: [
      { icon: '🏛️', title: 'Government & Enterprise', description: 'Experience with large-scale government and SOE projects. Compliance with Chinese cybersecurity standards.' },
      { icon: '🤖', title: 'AI Capital Expertise', description: 'Beijing is China\'s AI hub. Our teams build cutting-edge AI solutions for the capital\'s tech ecosystem.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Premium talent at competitive rates vs Beijing\'s high-cost market. Enterprise quality guaranteed.' },
      { icon: '🔒', title: 'Cybersecurity Compliance', description: 'MLPS 2.0, PIPL, CSL compliant. Secure development environments for sensitive projects.' },
      { icon: '🌐', title: 'Bilingual Teams', description: 'Mandarin and English-speaking engineers for international collaboration.' },
      { icon: '📈', title: 'Scalable Delivery', description: 'From MVP to enterprise platform — scale teams rapidly for Beijing-based organizations.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you work with Chinese government projects?', answer: 'We support government-adjacent projects and SOEs with compliant development practices, data localization, and security clearance-ready processes.' },
      { question: 'How do you handle MLPS 2.0 compliance?', answer: 'Our engineering practices align with China\'s Multi-Level Protection Scheme 2.0 for classified information systems.' },
      { question: 'What AI capabilities do you offer for Beijing clients?', answer: 'Computer vision, NLP (Chinese language), recommendation engines, predictive analytics, and LLM integration with both Chinese and international AI models.' },
      { question: 'Can you integrate with Baidu, Alibaba, Tencent clouds?', answer: 'Yes. We have experience with Baidu Cloud, Alibaba Cloud, Tencent Cloud, and Huawei Cloud alongside AWS and Azure.' },
      { question: 'What is the minimum engagement?', answer: 'Start with 2 engineers. Scale to 50+. Flexible monthly contracts with no long-term lock-in.' },
      { question: 'Do you support cross-border projects?', answer: 'Yes. We specialize in projects serving both Chinese domestic and international markets with proper data compliance.' },
    ],
    localRate: '¥900-1600',
    ourRate: '¥400-700',
    savings: '55%+',
  },

  shenzhen: {
    slug: 'software-development-agency-shenzhen',
    cityName: 'Shenzhen',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY/USD',
    title: {
      en: 'Software Development Agency Shenzhen | Call IT Dev',
      fr: 'Agence de Développement Informatique Shenzhen | Call IT Dev',
      zh: '深圳软件开发公司 | Call IT Dev',
      ja: 'ソフトウェア開発会社 深セン | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Shenzhen, China\'s Silicon Valley. Custom SaaS, IoT, hardware-software integration, AI. 200+ engineers.',
      fr: 'Agence de développement informatique pour Shenzhen. SaaS, IoT, intégration matériel-logiciel, IA. 200+ ingénieurs.',
      zh: '为深圳——中国硅谷提供软件开发服务。定制SaaS、物联网、软硬件集成、AI。200+工程师。',
    },
    keywords: ['software development agency Shenzhen', '深圳软件开发公司', 'IoT development Shenzhen', 'hardware-software integration Shenzhen', 'AI development Shenzhen', 'app development Shenzhen'],
    heroTitle: {
      en: 'Software Development Agency for Shenzhen',
      fr: 'Agence de Développement Informatique pour Shenzhen',
      zh: '深圳软件开发合作伙伴',
    },
    heroSubtitle: {
      en: 'China\'s hardware capital meets world-class software. IoT, embedded systems, and full-stack development for Shenzhen\'s innovation ecosystem.',
      fr: 'La capitale chinoise du hardware rencontre le logiciel de classe mondiale. IoT, systèmes embarqués et développement full-stack.',
      zh: '中国硬件之都遇上世界级软件。IoT、嵌入式系统和全栈开发，服务深圳创新生态。',
    },
    whyTitle: { en: 'Why Choose Our Agency for Shenzhen Projects', zh: '为什么选择我们的深圳项目团队' },
    whyReasons: [
      { icon: '🔧', title: 'Hardware-Software Integration', description: 'Shenzhen is the world\'s hardware capital. We build software for IoT devices, embedded systems, and smart products.' },
      { icon: '⚡', title: 'Innovation Speed', description: 'Match Shenzhen\'s legendary speed. Rapid prototyping, MVP in 4-6 weeks, iterative development.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Competitive rates vs Shenzhen\'s growing tech market. Premium quality at scale.' },
      { icon: '🌐', title: 'Cross-Border E-commerce', description: 'Expert in platforms serving both Chinese and global markets. Multi-currency, multi-language.' },
      { icon: '📱', title: 'Mobile-First', description: 'WeChat Mini Programs, native apps, cross-platform development for China\'s mobile-first market.' },
      { icon: '🏭', title: 'Manufacturing Tech', description: 'MES, ERP, supply chain software for Shenzhen\'s manufacturing ecosystem.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you build IoT software for Shenzhen hardware?', answer: 'Yes. We specialize in firmware, embedded software, IoT platforms, and cloud backends for Shenzhen-manufactured hardware products.' },
      { question: 'Can you build WeChat Mini Programs?', answer: 'Absolutely. Our teams have extensive WeChat Mini Program development experience for e-commerce, services, and enterprise applications.' },
      { question: 'How fast can you deliver an MVP?', answer: 'MVP in 4-6 weeks. Full product launch in 8-12 weeks. We match Shenzhen\'s speed-to-market culture.' },
      { question: 'Do you support cross-border e-commerce platforms?', answer: 'Yes. We build platforms for Chinese sellers on global markets and international brands entering China, with proper compliance.' },
      { question: 'What about data localization?', answer: 'Full PIPL compliance. We architect solutions with proper data residency for Chinese market requirements.' },
      { question: 'Can you integrate with Chinese payment systems?', answer: 'Yes — WeChat Pay, Alipay, UnionPay, and cross-border payment processors.' },
    ],
    localRate: '¥700-1300',
    ourRate: '¥350-600',
    savings: '50%+',
  },

  guangzhou: {
    slug: 'software-development-agency-guangzhou',
    cityName: 'Guangzhou',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY/USD',
    title: {
      en: 'Software Development Agency Guangzhou | Call IT Dev',
      fr: 'Agence de Développement Informatique Canton | Call IT Dev',
      zh: '广州软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Guangzhou, China. E-commerce, supply chain, AI solutions. 200+ engineers, competitive rates.',
      fr: 'Agence de développement informatique pour Canton, Chine. E-commerce, supply chain, IA. 200+ ingénieurs.',
      zh: '为广州提供软件开发服务。电商、供应链、AI解决方案。200+工程师，价格具有竞争力。',
    },
    keywords: ['software development agency Guangzhou', '广州软件开发公司', 'e-commerce development Guangzhou', 'supply chain software Guangzhou'],
    heroTitle: { en: 'Software Development Agency for Guangzhou', zh: '广州软件开发合作伙伴' },
    heroSubtitle: {
      en: 'Guangzhou\'s trade capital meets modern tech. E-commerce, supply chain, and enterprise software at competitive rates.',
      zh: '广州贸易之都遇上现代科技。电商、供应链和企业软件，价格极具竞争力。',
    },
    whyTitle: { en: 'Why Choose Our Agency for Guangzhou Projects', zh: '为什么选择我们的广州项目团队' },
    whyReasons: [
      { icon: '🛒', title: 'E-commerce Expertise', description: 'Canton Fair\'s digital transformation partner. E-commerce platforms for China\'s trade capital.' },
      { icon: '📦', title: 'Supply Chain Tech', description: 'WMS, TMS, SCM solutions for Guangzhou\'s massive logistics network.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Premium engineering at competitive rates vs Guangzhou market prices.' },
      { icon: '🌍', title: 'Trade & Export Platforms', description: 'B2B platforms, cross-border trade solutions, and digital export tools.' },
      { icon: '🏭', title: 'Manufacturing Integration', description: 'ERP, MES, and Industry 4.0 solutions for Pearl River Delta manufacturers.' },
      { icon: '📱', title: 'Cantonese-Speaking Teams', description: 'Engineers fluent in Cantonese, Mandarin, and English for local market understanding.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you specialize in e-commerce for Guangzhou?', answer: 'Yes. We build platforms for wholesale, B2B, cross-border trade, and retail e-commerce — core to Guangzhou\'s economy.' },
      { question: 'Can you integrate with Chinese logistics APIs?', answer: 'Yes — SF Express, ZTO, YTO, JD Logistics, and all major Chinese logistics providers.' },
      { question: 'Do you support Cantonese-speaking teams?', answer: 'Yes. We have team members fluent in Cantonese for seamless communication with Guangzhou businesses.' },
      { question: 'What about Canton Fair digital solutions?', answer: 'We build digital showrooms, virtual expo platforms, and B2B matching systems for Canton Fair exhibitors.' },
      { question: 'How do you handle multi-currency?', answer: 'Our platforms support CNY, USD, EUR, and 50+ currencies with real-time exchange rate integration.' },
      { question: 'What is the cost vs local Guangzhou agencies?', answer: 'Senior developers in Guangzhou cost ¥600-1200/hr. Our rate is ¥300-550/hr — 50%+ savings with the same quality.' },
    ],
    localRate: '¥600-1200',
    ourRate: '¥300-550',
    savings: '50%+',
  },

  chengdu: {
    slug: 'software-development-agency-chengdu',
    cityName: 'Chengdu',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY/USD',
    title: {
      en: 'Software Development Agency Chengdu | Call IT Dev',
      fr: 'Agence de Développement Informatique Chengdu | Call IT Dev',
      zh: '成都软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Chengdu, China\'s western tech hub. Gaming, AI, cloud solutions. 200+ engineers, competitive rates.',
      fr: 'Agence de développement informatique pour Chengdu. Jeux, IA, solutions cloud. 200+ ingénieurs.',
      zh: '为成都——中国西部科技中心提供软件开发服务。游戏、AI、云解决方案。200+工程师。',
    },
    keywords: ['software development agency Chengdu', '成都软件开发公司', 'gaming development Chengdu', 'AI development Chengdu'],
    heroTitle: { en: 'Software Development Agency for Chengdu', zh: '成都软件开发合作伙伴' },
    heroSubtitle: {
      en: 'China\'s western tech hub. Gaming, AI, and enterprise software development at Chengdu-competitive rates.',
      zh: '中国西部科技中心。游戏、AI和企业软件开发，成都竞争力价格。',
    },
    whyTitle: { en: 'Why Choose Our Agency for Chengdu Projects', zh: '为什么选择我们的成都项目团队' },
    whyReasons: [
      { icon: '🎮', title: 'Gaming Industry Hub', description: 'Chengdu is China\'s gaming capital. We build game backends, platforms, and gaming tech.' },
      { icon: '🤖', title: 'AI & Big Data', description: 'Leveraging Chengdu\'s AI talent pool for ML, data analytics, and intelligent systems.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Competitive rates in China\'s most cost-effective tech hub.' },
      { icon: '🎓', title: 'Top University Talent', description: 'Access to graduates from Sichuan University, UESTC, and other top institutions.' },
      { icon: '☁️', title: 'Cloud & Infrastructure', description: 'Major cloud data centers in Chengdu. Low-latency deployment for western China.' },
      { icon: '🌏', title: 'Belt & Road Gateway', description: 'Strategic position for projects serving Central and South Asia markets.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Is Chengdu good for gaming development?', answer: 'Chengdu is China\'s #2 gaming hub after Shanghai. We build game backends, real-time multiplayer systems, and gaming platforms.' },
      { question: 'What are the cost advantages of Chengdu?', answer: 'Chengdu offers 30-40% lower costs than Shanghai/Beijing with comparable talent quality. Our rates are even more competitive.' },
      { question: 'Do you work with Chengdu startups?', answer: 'Yes. Chengdu\'s startup ecosystem is booming. We offer flexible MVP-to-scale engagement models.' },
      { question: 'Can you support western China deployment?', answer: 'Yes. With major cloud providers having data centers in Chengdu, we ensure low-latency deployment for western China users.' },
      { question: 'What universities do your engineers come from?', answer: 'Sichuan University, UESTC (电子科技大学), Southwest Jiaotong University, and top international institutions.' },
      { question: 'Do you support Belt & Road projects?', answer: 'Yes. Chengdu is a strategic gateway for Belt & Road. We build platforms for cross-border trade and logistics.' },
    ],
    localRate: '¥500-1000',
    ourRate: '¥250-500',
    savings: '50%+',
  },

  hongkong: {
    slug: 'software-development-agency-hong-kong',
    cityName: 'Hong Kong',
    countryName: 'Hong Kong SAR',
    flag: '🇭🇰',
    timezone: 'HKT (GMT+8)',
    currency: 'HKD/USD',
    title: {
      en: 'Software Development Agency Hong Kong | Call IT Dev',
      fr: 'Agence de Développement Informatique Hong Kong | Call IT Dev',
      zh: '香港软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Hong Kong. FinTech, web, mobile, AI. 200+ engineers, 60% savings vs HK rates. ISO 27001.',
      fr: 'Agence de développement informatique pour Hong Kong. FinTech, web, mobile, IA. 200+ ingénieurs.',
      zh: '为香港提供软件开发服务。金融科技、Web、移动应用、AI。200+工程师，比香港价格节省60%。',
    },
    keywords: ['software development agency Hong Kong', '香港软件开发公司', 'fintech development Hong Kong', 'app development Hong Kong'],
    heroTitle: { en: 'Software Development Agency for Hong Kong', zh: '香港软件开发合作伙伴' },
    heroSubtitle: {
      en: 'Asia\'s financial capital deserves world-class tech. FinTech, RegTech, and enterprise software at 60% savings vs HK rates.',
      zh: '亚洲金融之都值得世界级技术。金融科技、监管科技和企业软件，比香港价格节省60%。',
    },
    whyTitle: { en: 'Why Hong Kong Companies Choose Call IT Dev', zh: '为什么香港公司选择Call IT Dev' },
    whyReasons: [
      { icon: '🏦', title: 'FinTech Expertise', description: 'Specialized in Hong Kong\'s financial services sector. PCI-DSS, SOC 2, HKMA compliance.' },
      { icon: '💰', title: '60% Cost Savings', description: 'HK$500-900/hr locally vs HK$200-400/hr with us. Same quality, massive savings.' },
      { icon: '🌐', title: 'East-West Bridge', description: 'Bilingual teams bridging Chinese and international markets from Hong Kong.' },
      { icon: '🔒', title: 'PDPO Compliant', description: 'Hong Kong Personal Data (Privacy) Ordinance compliant. Enterprise-grade security.' },
      { icon: '📊', title: 'RegTech Solutions', description: 'Compliance automation, KYC/AML platforms, and regulatory reporting for HK financial institutions.' },
      { icon: '⚡', title: 'Rapid Delivery', description: 'MVP in 4-6 weeks. Scale teams from 2 to 30+ engineers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you specialize in FinTech for Hong Kong?', answer: 'Yes. We build payment platforms, trading systems, robo-advisors, and compliance tools for HK\'s financial sector.' },
      { question: 'How do you handle HKMA regulations?', answer: 'Our teams understand HKMA guidelines, virtual banking requirements, and SFC regulations for financial technology.' },
      { question: 'What is the cost comparison to Hong Kong?', answer: 'Senior developers in HK cost HK$500-900/hr (agency). Our rate is HK$200-400/hr — 60% savings.' },
      { question: 'Can you support both Chinese and English interfaces?', answer: 'Yes. Traditional Chinese, Simplified Chinese, and English — fully localized for the Hong Kong market.' },
      { question: 'Do you comply with PDPO?', answer: 'Yes. Full compliance with Hong Kong\'s Personal Data (Privacy) Ordinance and cross-border data transfer requirements.' },
      { question: 'How quickly can you onboard?', answer: 'Team ready in 2 weeks. Technical interviews within 5 business days. First sprint starts week 3.' },
    ],
    localRate: 'HK$500-900',
    ourRate: 'HK$200-400',
    savings: '60%+',
  },

  taipei: {
    slug: 'software-development-agency-taipei',
    cityName: 'Taipei',
    countryName: 'Taiwan',
    flag: '🇹🇼',
    timezone: 'CST (GMT+8)',
    currency: 'TWD/USD',
    title: {
      en: 'Software Development Agency Taipei | Call IT Dev',
      fr: 'Agence de Développement Informatique Taipei | Call IT Dev',
      zh: '台北软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Taipei, Taiwan. Semiconductor tech, SaaS, web, mobile, AI. 200+ engineers, competitive rates.',
      fr: 'Agence de développement informatique pour Taipei, Taïwan. Semi-conducteurs, SaaS, web, mobile, IA.',
      zh: '为台北提供软件开发服务。半导体技术、SaaS、Web、移动应用、AI。200+工程师。',
    },
    keywords: ['software development agency Taipei', '台北软件開發公司', 'IT outsourcing Taiwan', 'semiconductor software Taipei'],
    heroTitle: { en: 'Software Development Agency for Taipei', zh: '台北软件开发合作伙伴' },
    heroSubtitle: {
      en: 'Taiwan\'s semiconductor capital meets enterprise software. Deep tech, AI, and full-stack development at competitive rates.',
      zh: '台湾半导体之都遇上企业软件。深度科技、AI和全栈开发，价格极具竞争力。',
    },
    whyTitle: { en: 'Why Taipei Companies Choose Call IT Dev', zh: '为什么台北公司选择Call IT Dev' },
    whyReasons: [
      { icon: '💎', title: 'Deep Tech Expertise', description: 'Software for semiconductor, IoT, and advanced manufacturing — Taipei\'s core industries.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Premium engineering at half the cost of Taipei agencies. Enterprise quality guaranteed.' },
      { icon: '🌐', title: 'Mandarin-Speaking Teams', description: 'Native Mandarin communication for seamless Taipei collaboration.' },
      { icon: '🔬', title: 'R&D Software', description: 'Lab management, simulation, and engineering software for Taiwan\'s R&D sector.' },
      { icon: '📱', title: 'Full-Stack Delivery', description: 'From embedded firmware to cloud platforms — complete tech stack coverage.' },
      { icon: '🏭', title: 'Industry 4.0', description: 'Smart factory solutions for Taiwan\'s advanced manufacturing ecosystem.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you work with semiconductor companies?', answer: 'Yes. We build EDA tools, yield management software, and supply chain platforms for the semiconductor industry.' },
      { question: 'Can you support Traditional Chinese?', answer: 'Yes. Full Traditional Chinese (繁體中文) localization for Taipei/Taiwan market.' },
      { question: 'What about Taiwan data privacy laws?', answer: 'We comply with Taiwan\'s PDPA and international standards (GDPR, ISO 27001).' },
      { question: 'How do you handle cross-strait projects?', answer: 'We support both mainland China and Taiwan markets with proper regulatory compliance for each jurisdiction.' },
      { question: 'What tech stack for Industry 4.0?', answer: 'IoT platforms, SCADA integration, MES systems, digital twins, and predictive maintenance solutions.' },
      { question: 'Minimum team size?', answer: 'Start with 2 developers. Scale to 30+. Flexible engagement models.' },
    ],
    localRate: 'NT$2500-5000',
    ourRate: 'NT$1200-2500',
    savings: '50%+',
  },

  hyderabad: {
    slug: 'software-development-agency-hyderabad',
    cityName: 'Hyderabad',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: {
      en: 'Software Development Agency Hyderabad | Call IT Dev',
      fr: 'Agence de Développement Informatique Hyderabad | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Hyderabad, India\'s pharma & IT hub. SaaS, AI, cloud. 200+ engineers, competitive rates.',
      fr: 'Agence de développement informatique pour Hyderabad, hub IT et pharma indien. SaaS, IA, cloud.',
    },
    keywords: ['software development agency Hyderabad', 'IT outsourcing Hyderabad', 'pharma software Hyderabad', 'AI development Hyderabad'],
    heroTitle: { en: 'Software Development Agency for Hyderabad' },
    heroSubtitle: { en: 'India\'s pharma and IT capital. Enterprise software, AI, and cloud solutions at competitive global rates.' },
    whyTitle: { en: 'Why Hyderabad Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '💊', title: 'Pharma & Life Sciences', description: 'Software for Hyderabad\'s pharmaceutical industry: LIMS, clinical trials, regulatory compliance.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Premium engineering complementing Hyderabad\'s tech ecosystem at competitive global prices.' },
      { icon: '🏢', title: 'Enterprise Grade', description: 'Microsoft, Google, Amazon all have major campuses here. We match their engineering standards.' },
      { icon: '🌐', title: 'English-Speaking Teams', description: 'Fluent English communication for seamless Hyderabad collaboration.' },
      { icon: '☁️', title: 'Cloud-Native', description: 'AWS, Azure, GCP expertise for Hyderabad\'s cloud-first enterprises.' },
      { icon: '🔒', title: 'Compliance Ready', description: 'HIPAA, SOC 2, ISO 27001 for healthcare and enterprise clients.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you specialize in pharma software?', answer: 'Yes. LIMS, clinical trial management, regulatory submissions, and pharmacovigilance platforms.' },
      { question: 'How do you compare to Hyderabad IT companies?', answer: 'We offer competitive rates with nearshore advantages: CET timezone overlap with European clients, multilingual teams.' },
      { question: 'Can you augment existing Hyderabad teams?', answer: 'Yes. Staff augmentation is a core offering. Our engineers integrate into your existing workflows.' },
      { question: 'What about healthcare compliance?', answer: 'HIPAA compliant, GxP validated, 21 CFR Part 11 ready for pharmaceutical clients.' },
      { question: 'Do you support AI/ML projects?', answer: 'Yes. Deep learning, NLP, computer vision, and MLOps for enterprise AI initiatives.' },
      { question: 'Minimum engagement?', answer: 'Start with 2 engineers. Flexible monthly contracts. Scale as needed.' },
    ],
    localRate: '₹3000-6000',
    ourRate: '₹1500-3000',
    savings: '50%+',
  },

  telaviv: {
    slug: 'software-development-agency-tel-aviv',
    cityName: 'Tel Aviv',
    countryName: 'Israel',
    flag: '🇮🇱',
    timezone: 'IST (GMT+2)',
    currency: 'ILS/USD',
    title: {
      en: 'Software Development Agency Tel Aviv | Call IT Dev',
      fr: 'Agence de Développement Informatique Tel Aviv | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Tel Aviv, Israel\'s startup capital. SaaS, cybersecurity, AI. 200+ engineers, 55% savings vs TLV rates.',
      fr: 'Agence de développement informatique pour Tel Aviv. SaaS, cybersécurité, IA. 200+ ingénieurs, 55% d\'économies.',
    },
    keywords: ['software development agency Tel Aviv', 'IT outsourcing Israel', 'startup development Tel Aviv', 'cybersecurity development Israel'],
    heroTitle: { en: 'Software Development Agency for Tel Aviv' },
    heroSubtitle: { en: 'The Startup Nation\'s tech capital meets world-class nearshore engineering. SaaS, cybersecurity, and AI at 55% savings vs Tel Aviv rates.' },
    whyTitle: { en: 'Why Tel Aviv Startups Choose Call IT Dev' },
    whyReasons: [
      { icon: '🚀', title: 'Startup DNA', description: 'We understand startup velocity. MVP in 4 weeks, Series A-ready products, rapid iteration.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at $35-55/hr vs $80-150/hr in Tel Aviv. Same startup energy, better economics.' },
      { icon: '🔒', title: 'Cybersecurity', description: 'Israel-grade security practices. Pen testing, AppSec, and compliance built into every project.' },
      { icon: '⏰', title: 'Same Timezone', description: 'Morocco is GMT+1, just 1 hour from Israel. Real-time collaboration.' },
      { icon: '🤖', title: 'AI & Deep Tech', description: 'Computer vision, NLP, ML platforms — matching Tel Aviv\'s innovation standards.' },
      { icon: '📈', title: 'Scale-Up Support', description: 'From seed to Series C — we scale engineering teams as you grow.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you work with Israeli startups?', answer: 'Yes. We\'ve supported seed-stage to Series B+ Israeli startups with full-stack development and staff augmentation.' },
      { question: 'How does the timezone work?', answer: 'Morocco (GMT+1) is just 1 hour behind Israel. Full daily overlap for real-time collaboration.' },
      { question: 'What about cybersecurity projects?', answer: 'We build security tools, SIEM platforms, threat intelligence systems, and AppSec solutions for the Israeli market.' },
      { question: 'Can you handle rapid scaling?', answer: 'Yes. Start with 2 devs this week, scale to 20+ within a month. Startup-speed onboarding.' },
      { question: 'Do you sign Israeli law contracts?', answer: 'Yes. We can work under Israeli law contracts with proper IP assignment and NDA provisions.' },
      { question: 'What is the cost comparison?', answer: 'Tel Aviv senior dev: $80-150/hr. Our rate: $35-55/hr. 55% savings with equivalent quality.' },
    ],
    localRate: '$80-150',
    ourRate: '$35-55',
    savings: '55%+',
  },

  denver: {
    slug: 'software-development-agency-denver',
    cityName: 'Denver',
    countryName: 'United States',
    flag: '🇺🇸',
    timezone: 'MST (GMT-7)',
    currency: 'USD',
    title: {
      en: 'Software Development Agency Denver | Call IT Dev',
      fr: 'Agence de Développement Informatique Denver | Call IT Dev',
      es: 'Agencia Desarrollo Software Denver | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Denver, Colorado. SaaS, web, mobile, AI. 200+ engineers, 65% savings vs Denver rates. ISO 27001.',
      fr: 'Agence de développement informatique pour Denver, Colorado. SaaS, web, mobile, IA. 65% d\'économies.',
      es: 'Agencia de desarrollo de software para Denver. SaaS, web, móvil, IA. 65% ahorro vs tarifas Denver.',
    },
    keywords: ['software development agency Denver', 'IT outsourcing Denver', 'nearshore development Colorado', 'app development Denver'],
    heroTitle: { en: 'Software Development Agency for Denver' },
    heroSubtitle: { en: 'Colorado\'s booming tech scene deserves world-class engineering. SaaS, AI, and enterprise software at 65% savings vs Denver rates.' },
    whyTitle: { en: 'Why Denver Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🏔️', title: 'Colorado Tech Hub', description: 'Supporting Denver\'s growing tech ecosystem with enterprise-grade engineering teams.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at $35-55/hr vs $100-175/hr in Denver. Same quality, massive savings.' },
      { icon: '🌐', title: 'English-Speaking Teams', description: 'Fluent English communication. Same work culture and agile methodology.' },
      { icon: '⏰', title: 'Timezone Overlap', description: '6-8 hours of daily overlap with MST. Morning standups work perfectly.' },
      { icon: '🚀', title: 'Startup-Friendly', description: 'From MVP to scale — flexible engagement for Denver\'s startup ecosystem.' },
      { icon: '🔒', title: 'SOC 2 Ready', description: 'Enterprise security standards for Denver\'s regulated industries.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does the timezone work with Denver?', answer: 'Morocco is GMT+1, Denver is GMT-7. We have 6-8 hours of overlap. Morning standups at 9am MST work well.' },
      { question: 'Do you work with Colorado startups?', answer: 'Yes. Flexible MVP-to-scale engagements for Denver\'s growing startup ecosystem.' },
      { question: 'What industries in Denver do you serve?', answer: 'Aerospace, cannabis tech, fintech, healthtech, and outdoor recreation technology.' },
      { question: 'What is the cost comparison?', answer: 'Denver senior dev: $100-175/hr (agency). Our rate: $35-55/hr. 65% savings.' },
      { question: 'Can you work under US contracts?', answer: 'Yes. US law contracts, IP assignment, NDA, and SOC 2 compliance.' },
      { question: 'How fast can you start?', answer: 'Team ready in 2 weeks. CVs within 5 business days. First sprint week 3.' },
    ],
    localRate: '$100-175',
    ourRate: '$35-55',
    savings: '65%+',
  },
};
